import type {
  TimelineAction,
  TimelineHistory,
  TimelineProject,
  TimelineClip,
  TimelineTransition,
  AspectRatio,
} from "./timeline";
import {
  MAX_UNDO_HISTORY,
  MIN_CLIP_DURATION,
  MIN_SPEED,
  MAX_SPEED,
  MIN_TRANSITION_DURATION,
  MAX_TRANSITION_DURATION,
  ASPECT_RATIO_DIMENSIONS,
} from "./timeline";
import {
  getClipDuration,
  getClipStartTime,
  getTotalDuration,
  splitClipAtTime,
  generateId,
  clamp,
} from "./timeline-utils";

// ─── Helpers ────────────────────────────────────────────────────────────────

function applyToProject(
  history: TimelineHistory,
  updater: (project: TimelineProject) => TimelineProject,
): TimelineHistory {
  const past = [...history.past, history.present].slice(-MAX_UNDO_HISTORY);
  return { past, present: updater(history.present), future: [] };
}

function createCutTransition(): TimelineTransition {
  return { id: generateId(), type: "cut", duration: 0 };
}

// Normalize the payload for SET_INTRO/OUTRO_TRANSITION: `null` and
// `cut`-typed transitions both clear the field; valid non-cut types
// have their duration clamped to the same range as between-clip
// transitions. Returning `null` (rather than a cut-transition object)
// keeps the field tri-state — absent / cut / configured — without
// forcing every consumer to special-case both `null` and `cut`.
function normalizeBookendTransition(
  transition: TimelineTransition | null,
): TimelineTransition | null {
  if (!transition || transition.type === "cut") return null;
  const duration = Math.max(
    MIN_TRANSITION_DURATION,
    Math.min(MAX_TRANSITION_DURATION, transition.duration),
  );
  return { ...transition, duration };
}

function effectiveTransitionDuration(
  transition: TimelineTransition | undefined,
  left: TimelineClip | undefined,
  right: TimelineClip | undefined,
): number {
  if (!transition || transition.type === "cut" || transition.duration <= 0 || !left || !right) {
    return 0;
  }
  return Math.min(transition.duration, getClipDuration(left), getClipDuration(right));
}

function transitionKey(left: TimelineClip, right: TimelineClip): string {
  return `${left.id}\u0000${right.id}`;
}

function reconcileTransitionsAfterReorder(
  previousClips: TimelineClip[],
  nextClips: TimelineClip[],
  previousTransitions: TimelineTransition[],
): TimelineTransition[] {
  const previousByBoundary = new Map<string, TimelineTransition>();
  for (let i = 0; i < previousClips.length - 1; i++) {
    const transition = previousTransitions[i];
    if (transition) {
      previousByBoundary.set(
        transitionKey(previousClips[i], previousClips[i + 1]),
        transition,
      );
    }
  }

  const transitions: TimelineTransition[] = [];
  for (let i = 0; i < nextClips.length - 1; i++) {
    transitions.push(
      previousByBoundary.get(transitionKey(nextClips[i], nextClips[i + 1])) ??
        createCutTransition(),
    );
  }

  return transitions;
}

// Pack clips end-to-end on the timeline: the first clip starts at t=0,
// each subsequent clip starts exactly at `previousEnd - transitionOverlap`.
// This enforces the "no-gaps" invariant — `timelineStart` on incoming
// clips is treated as ordering intent only, not exact position. The
// dragged-clip's intended position is what determined the array order
// (via the caller's sort step); this pass just commits the packed
// layout.
function normalizeExplicitClipStarts(
  clips: TimelineClip[],
  transitions: TimelineTransition[],
): TimelineClip[] {
  let previousEnd = 0;
  return clips.map((clip, index) => {
    const transitionOverlap = index > 0
      ? effectiveTransitionDuration(transitions[index - 1], clips[index - 1], clip)
      : 0;
    const timelineStart = index === 0
      ? 0
      : Math.max(0, previousEnd - transitionOverlap);
    previousEnd = timelineStart + getClipDuration(clip);
    return { ...clip, timelineStart };
  });
}

// ─── Reducer ────────────────────────────────────────────────────────────────

export function timelineReducer(
  history: TimelineHistory,
  action: TimelineAction,
): TimelineHistory {
  switch (action.type) {
    case "UNDO": {
      if (history.past.length === 0) return history;
      const previous = history.past[history.past.length - 1];
      return {
        past: history.past.slice(0, -1),
        present: previous,
        future: [history.present, ...history.future],
      };
    }

    case "REDO": {
      if (history.future.length === 0) return history;
      const next = history.future[0];
      return {
        past: [...history.past, history.present],
        present: next,
        future: history.future.slice(1),
      };
    }

    case "LOAD_PROJECT": {
      return {
        past: [],
        present: action.project,
        future: [],
      };
    }

    case "ADD_CLIP": {
      return applyToProject(history, (project) => {
        const inserted = [...project.clips];
        const previousClipCount = inserted.length;
        const idx = clamp(action.atIndex, 0, inserted.length);
        const inferredTimelineStart = idx < inserted.length
          ? getClipStartTime(project, idx)
          : getTotalDuration(project);
        const clip = action.clip.timelineStart != null
          ? action.clip
          : { ...action.clip, timelineStart: inferredTimelineStart };
        inserted.splice(idx, 0, clip);
        const transitions = [...project.transitions];
        if (previousClipCount > 0) {
          if (idx === 0) {
            transitions.splice(0, 0, createCutTransition());
          } else if (idx === previousClipCount) {
            transitions.splice(previousClipCount - 1, 0, createCutTransition());
          } else {
            transitions.splice(idx - 1, 1, createCutTransition(), createCutTransition());
          }
        }
        return {
          ...project,
          clips: normalizeExplicitClipStarts(inserted, transitions),
          transitions,
        };
      });
    }

    case "DELETE_CLIP": {
      return applyToProject(history, (project) => {
        const clipIdx = project.clips.findIndex(
          (c) => c.id === action.clipId,
        );
        if (clipIdx === -1) return project;
        const filtered = project.clips.filter((c) => c.id !== action.clipId);
        if (filtered.length === 0) {
          return {
            ...project,
            clips: filtered,
            transitions: [],
            textOverlays: [],
            audioTracks: [],
          };
        }
        let transitions: TimelineTransition[];
        if (filtered.length <= 1) {
          transitions = [];
        } else if (clipIdx === 0) {
          transitions = project.transitions.slice(1);
        } else if (clipIdx === project.clips.length - 1) {
          transitions = project.transitions.slice(0, -1);
        } else {
          transitions = [
            ...project.transitions.slice(0, clipIdx - 1),
            createCutTransition(),
            ...project.transitions.slice(clipIdx + 1),
          ];
        }
        const clips = normalizeExplicitClipStarts(filtered, transitions);
        // Overlays and audio tracks past the shortened end are deliberately
        // KEPT: out-of-range items are harmless downstream (the playback
        // timing gate hides overlays; amix duration=first never plays late
        // audio), and dropping them here would silently destroy user data
        // that should survive the timeline growing again (e.g. delete a
        // clip, then add a replacement).
        return { ...project, clips, transitions };
      });
    }

    case "REORDER_CLIPS": {
      return applyToProject(history, (project) => {
        const { fromIndex, toIndex } = action;
        if (
          fromIndex < 0 ||
          fromIndex >= project.clips.length ||
          toIndex < 0 ||
          toIndex >= project.clips.length
        ) {
          return project;
        }
        const reordered = [...project.clips];
        const [moved] = reordered.splice(fromIndex, 1);
        reordered.splice(toIndex, 0, moved);
        const transitions = reconcileTransitionsAfterReorder(
          project.clips,
          reordered,
          project.transitions,
        );
        const clips = normalizeExplicitClipStarts(reordered, transitions);
        return { ...project, clips, transitions };
      });
    }

    case "MOVE_CLIP": {
      return applyToProject(history, (project) => {
        let movedOriginalIndex = -1;
        const clips = project.clips.map((c, index) => {
          if (c.id !== action.clipId) return c;
          movedOriginalIndex = index;
          return { ...c, timelineStart: Math.max(0, action.timelineStart) };
        });
        if (movedOriginalIndex === -1) return project;

        const orderedClips = clips
          .map((clip, index) => ({ clip, index }))
          .sort((a, b) => {
            const startDelta =
              getClipStartTime({ ...project, clips }, a.index) -
              getClipStartTime({ ...project, clips }, b.index);
            return startDelta !== 0 ? startDelta : a.index - b.index;
          })
          .map(({ clip }) => clip);

        const transitions = reconcileTransitionsAfterReorder(
          project.clips,
          orderedClips,
          project.transitions,
        );

        return {
          ...project,
          clips: normalizeExplicitClipStarts(orderedClips, transitions),
          transitions,
        };
      });
    }

    case "TRIM_CLIP": {
      return applyToProject(history, (project) => {
        const updated = project.clips.map((c) => {
          if (c.id !== action.clipId) return c;
          const trimStart = clamp(
            action.trimStart,
            0,
            c.sourceDuration - MIN_CLIP_DURATION,
          );
          const trimEnd = clamp(
            action.trimEnd,
            trimStart + MIN_CLIP_DURATION,
            c.sourceDuration,
          );
          return {
            ...c,
            ...(action.timelineStart != null ? { timelineStart: action.timelineStart } : {}),
            trimStart,
            trimEnd,
          };
        });
        return {
          ...project,
          clips: normalizeExplicitClipStarts(updated, project.transitions),
        };
      });
    }

    case "SPLIT_CLIP": {
      return applyToProject(history, (project) => {
        const clipIdx = project.clips.findIndex(
          (c) => c.id === action.clipId,
        );
        if (clipIdx === -1) return project;
        const clip = project.clips[clipIdx];
        const result = splitClipAtTime(clip, action.atTime);
        if (!result) return project;
        const [left, right] = result;
        if (clip.timelineStart == null) {
          left.timelineStart = getClipStartTime(project, clipIdx);
          right.timelineStart = left.timelineStart + getClipDuration(left);
        }
        const split = [...project.clips];
        split.splice(clipIdx, 1, left, right);
        // Insert a "cut" transition between the two new clips
        const transitions = [...project.transitions];
        transitions.splice(clipIdx, 0, createCutTransition());
        return {
          ...project,
          clips: normalizeExplicitClipStarts(split, transitions),
          transitions,
        };
      });
    }

    case "SET_CLIP_SPEED": {
      return applyToProject(history, (project) => {
        const updated = project.clips.map((c) =>
          c.id === action.clipId
            ? { ...c, speed: clamp(action.speed, MIN_SPEED, MAX_SPEED) }
            : c,
        );
        return {
          ...project,
          clips: normalizeExplicitClipStarts(updated, project.transitions),
        };
      });
    }

    case "RESIZE_IMAGE_CLIP": {
      return applyToProject(history, (project) => {
        const updated = project.clips.map((c) => {
          if (c.id !== action.clipId || c.type !== "image") return c;
          return {
            ...c,
            timelineStart: Math.max(0, action.timelineStart),
            displayDuration: Math.max(MIN_CLIP_DURATION, action.displayDuration),
          };
        });
        return {
          ...project,
          clips: normalizeExplicitClipStarts(updated, project.transitions),
        };
      });
    }

    case "SET_CLIP_VOLUME": {
      return applyToProject(history, (project) => ({
        ...project,
        clips: project.clips.map((c) =>
          c.id === action.clipId
            ? { ...c, volume: clamp(action.volume, 0, 1) }
            : c,
        ),
      }));
    }

    case "SET_CLIP_FILTER": {
      return applyToProject(history, (project) => ({
        ...project,
        clips: project.clips.map((c) =>
          c.id === action.clipId ? { ...c, filter: action.filter } : c,
        ),
      }));
    }

    case "SET_CLIP_FRAMING": {
      return applyToProject(history, (project) => ({
        ...project,
        clips: project.clips.map((c) =>
          c.id === action.clipId
            ? { ...c, framing: action.framing ?? undefined }
            : c,
        ),
      }));
    }

    case "SET_CLIP_GREEN_SCREEN": {
      return applyToProject(history, (project) => ({
        ...project,
        clips: project.clips.map((c) =>
          c.id === action.clipId
            ? { ...c, greenScreen: action.greenScreen }
            : c,
        ),
      }));
    }

    case "UPDATE_CLIP_GREEN_SCREEN": {
      return applyToProject(history, (project) => ({
        ...project,
        clips: project.clips.map((c) => {
          if (c.id !== action.clipId || !c.greenScreen) return c;
          return {
            ...c,
            greenScreen: { ...c.greenScreen, ...action.patch },
          };
        }),
      }));
    }

    case "SET_TRANSITION": {
      return applyToProject(history, (project) => {
        if (action.index < 0 || action.index >= project.clips.length - 1) {
          return project;
        }
        const transitions = [...project.transitions];
        // Pad with "cut" transitions if needed
        while (transitions.length <= action.index) {
          transitions.push(createCutTransition());
        }
        transitions[action.index] = action.transition;
        // Pack-tight handles the positional consequence of the new
        // transition's overlap: previous bespoke delta math is now
        // redundant (and was fragile against the rest of the pack-tight
        // reducer cases). `normalizeExplicitClipStarts` recomputes every
        // clip's `timelineStart` from the new transition set in one pass.
        return {
          ...project,
          clips: normalizeExplicitClipStarts(project.clips, transitions),
          transitions,
        };
      });
    }

    case "SET_INTRO_TRANSITION": {
      return applyToProject(history, (project) => ({
        ...project,
        introTransition: normalizeBookendTransition(action.transition),
      }));
    }

    case "SET_OUTRO_TRANSITION": {
      return applyToProject(history, (project) => ({
        ...project,
        outroTransition: normalizeBookendTransition(action.transition),
      }));
    }

    case "ADD_TEXT_OVERLAY": {
      return applyToProject(history, (project) => ({
        ...project,
        textOverlays: [...project.textOverlays, action.overlay],
      }));
    }

    case "UPDATE_TEXT_OVERLAY": {
      return applyToProject(history, (project) => ({
        ...project,
        textOverlays: project.textOverlays.map((o) =>
          o.id === action.id ? { ...o, ...action.patch } : o,
        ),
      }));
    }

    case "DELETE_TEXT_OVERLAY": {
      return applyToProject(history, (project) => ({
        ...project,
        textOverlays: project.textOverlays.filter((o) => o.id !== action.id),
      }));
    }

    case "ADD_AUDIO_TRACK": {
      return applyToProject(history, (project) => ({
        ...project,
        audioTracks: [...project.audioTracks, action.track],
      }));
    }

    case "UPDATE_AUDIO_TRACK": {
      return applyToProject(history, (project) => ({
        ...project,
        audioTracks: project.audioTracks.map((t) =>
          t.id === action.id ? { ...t, ...action.patch } : t,
        ),
      }));
    }

    case "DELETE_AUDIO_TRACK": {
      return applyToProject(history, (project) => ({
        ...project,
        audioTracks: project.audioTracks.filter((t) => t.id !== action.id),
      }));
    }

    case "SET_ASPECT_RATIO": {
      return applyToProject(history, (project) => {
        const dims = ASPECT_RATIO_DIMENSIONS[action.aspectRatio];
        return {
          ...project,
          aspectRatio: action.aspectRatio,
          outputWidth: dims.width,
          outputHeight: dims.height,
        };
      });
    }

    case "SET_PROJECT_NAME": {
      // Name lives outside the undo timeline — keystroke-level edits
      // would flood the stack, and an unrelated undo shouldn't revert
      // the title. Sync across past/present/future so name is never
      // affected by UNDO/REDO.
      const setName = (p: TimelineProject) => ({ ...p, name: action.name });
      return {
        past: history.past.map(setName),
        present: setName(history.present),
        future: history.future.map(setName),
      };
    }

    // Preview actions: modify present without pushing to undo history.
    // Used during drag operations (trim handles, overlay resize).
    case "TRIM_CLIP_PREVIEW": {
      return {
        ...history,
        present: {
          ...history.present,
          clips: history.present.clips.map((c) => {
            if (c.id !== action.clipId) return c;
            const trimStart = clamp(action.trimStart, 0, c.sourceDuration - MIN_CLIP_DURATION);
            const trimEnd = clamp(action.trimEnd, trimStart + MIN_CLIP_DURATION, c.sourceDuration);
            return {
              ...c,
              ...(action.timelineStart != null ? { timelineStart: action.timelineStart } : {}),
              trimStart,
              trimEnd,
            };
          }),
        },
      };
    }

    case "UPDATE_TEXT_OVERLAY_PREVIEW": {
      return {
        ...history,
        present: {
          ...history.present,
          textOverlays: history.present.textOverlays.map((o) =>
            o.id === action.id ? { ...o, ...action.patch } : o,
          ),
        },
      };
    }

    case "UPDATE_AUDIO_TRACK_PREVIEW": {
      return {
        ...history,
        present: {
          ...history.present,
          audioTracks: history.present.audioTracks.map((t) =>
            t.id === action.id ? { ...t, ...action.patch } : t,
          ),
        },
      };
    }

    default:
      return history;
  }
}

// ─── Factory ────────────────────────────────────────────────────────────────

export function createTimelineHistory(
  project: TimelineProject,
): TimelineHistory {
  return {
    past: [],
    present: project,
    future: [],
  };
}
